import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSignupRider } from "../../hooks/auth-operations/useAuthMutations";
import useValidateForm from "../../hooks/useValidateForm";
import { useAuthStore, useUserStore } from "../../stores";
import { useLookupInviteCode } from "../../hooks/invite-operations/useInviteQueries";

function Signup() {
  // Global state.
  const { setAccessToken, setIsAuth } = useAuthStore();
  const { setUserId, setUserType, setUserName } = useUserStore();

  // Get invite code from URL params
  const [searchParams] = useSearchParams();
  const inviteCodeParam = searchParams.get("invite");

  // Look up the invite code
  const { inviteCodeLookupData, loadingInviteCodeLookup } = useLookupInviteCode(inviteCodeParam);

  // State for referral info
  const [referralInfo, setReferralInfo] = useState(null);

  // State for signup form.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Set referral info when invite code lookup completes
  useEffect(() => {
    if (inviteCodeLookupData?.lookupInviteCode?.valid) {
      setReferralInfo({
        driverId: inviteCodeLookupData.lookupInviteCode.driverId,
        driverName: inviteCodeLookupData.lookupInviteCode.driverName,
      });
    }
  }, [inviteCodeLookupData]);

  // Set up route navigation.
  const navigate = useNavigate();

  // Use the form validation hook.
  const { register, handleSubmit, formState: { errors }, handlePhoneChange } =
    useValidateForm("sign-up", null);

  // Get signup rider mutation.
  const { signupRider, loadingRiderSignup } = useSignupRider();

  // Signup Submit.
  async function handleSignupSubmit(data) {
    setErrorMessage("");

    try {
      // Format phone number to ensure (XXX) XXX-XXXX format
      let numericPhone = data.phone.replace(/\D/g, '');
      // Strip leading country code '1' if present (e.g. +1 auto-filled)
      if (numericPhone.length === 11 && numericPhone.startsWith('1')) {
        numericPhone = numericPhone.slice(1);
      }
      const formattedPhone = numericPhone.replace(
        /^(\d{3})(\d{3})(\d{4})$/,
        '($1) $2-$3'
      );

      // Build variables, including invite code if valid. Trim name/email —
      // stray spaces from autofill or mobile keyboards fail validation.
      const variables = {
        name: data.name.trim(),
        phoneNumber: formattedPhone,
        email: data.email.trim(),
        password: data.password,
      };

      // Only include invite code if it's valid
      if (referralInfo?.driverId && inviteCodeParam) {
        variables.inviteCode = inviteCodeParam;
      }

      const response = await signupRider({
        variables,
      });

      const userData = response.data.signupRider;

      // Set global state.
      setAccessToken(userData.accessToken);
      setIsAuth(true);
      setUserId(userData.rider.id);
      setUserType("rider");
      setUserName(userData.rider.name);

      // Navigate to the rider home page (signup is rider-only) — matches
      // the login flow. NOTE: "/profile" doesn't exist and 404'd.
      navigate("/riderhome");
    } catch (error) {
      console.error("Signup error:", error);
      if (error.message.includes("DUPLICATE_EMAIL") || error.message.includes("already exists")) {
        setErrorMessage("An account with this email already exists.");
      } else {
        setErrorMessage("An error occurred during signup. Please try again.");
      }
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(handleSignupSubmit)}
        className="flex flex-col w-full center-all"
      >
        <h3 className="text-highlight-blue pt-6 pb-4 text-xl">Sign Up</h3>

        {/* Referral banner - shows when invited by a driver */}
        {referralInfo && (
          <div className="w-4/5 mb-4 p-3 bg-main-green-dark/20 border border-main-green-light/30 rounded-lg">
            <p className="text-sm text-main-green-light text-center">
              You were invited by <span className="font-semibold">{referralInfo.driverName}</span>
            </p>
            <p className="text-xs text-main-light-highlight text-center mt-1">
              They'll be added to your favorite drivers!
            </p>
          </div>
        )}

        <input
          type="text"
          id="signup-name"
          name="signup-name"
          autoComplete="name"
          placeholder="Name"
          className={`modal-input-layout modal-input-border ${
            errors.name ? "modal-input-error" : ""
          }`}
          {...register("name")}
        />
        <p className="error-text-light">{errors.name?.message}</p>

        <input
          type="tel"
          id="signup-phone"
          name="signup-phone"
          autoComplete="tel"
          placeholder="Phone Number"
          className={`modal-input-layout modal-input-border ${
            errors.phone ? "modal-input-error" : ""
          }`}
          {...register("phone", { onChange: handlePhoneChange })}
        />
        <p className="error-text-light">{errors.phone?.message}</p>

        <input
          type="email"
          id="signup-email"
          name="signup-email"
          autoComplete="email"
          placeholder="Email"
          className={`modal-input-layout modal-input-border ${
            errors.email ? "modal-input-error" : ""
          }`}
          {...register("email")}
        />
        <p className="error-text-light">{errors.email?.message}</p>

        <div className="relative w-4/5 m-4">
          <input
            type={showPassword ? "text" : "password"}
            id="signup-password"
            name="signup-password"
            autoComplete="new-password"
            placeholder="Password"
            className={`text-black p-2 rounded w-full modal-input-border ${
              errors.password ? "modal-input-error" : ""
            }`}
            {...register("password")}
          />
          <FaEye
            className="absolute cursor-pointer top-2.5 right-2.5 w-6 h-6 text-black"
            onClick={() => setShowPassword((prevState) => !prevState)}
          />
        </div>
        <p className="error-text-light">{errors.password?.message}</p>

        <div className="relative w-4/5 m-4">
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="signup-confirm-password"
            name="signup-confirm-password"
            autoComplete="new-password"
            placeholder="Confirm Password"
            className={`text-black p-2 rounded w-full modal-input-border ${
              errors.confirmpassword ? "modal-input-error" : ""
            }`}
            {...register("confirmpassword")}
          />
          <FaEye
            className="absolute cursor-pointer top-2.5 right-2.5 w-6 h-6 text-black"
            onClick={() => setShowConfirmPassword((prevState) => !prevState)}
          />
        </div>
        <p className="error-text-light">{errors.confirmpassword?.message}</p>

        <button
          type="submit"
          className="btn btn-green btn-md"
          disabled={loadingRiderSignup}
        >
          {loadingRiderSignup ? "Signing up..." : "Sign Up"}
        </button>
      </form>
      {errorMessage && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-500">
          {errorMessage}
        </p>
      )}
    </>
  );
}

export default Signup;
